import type { RouteNamedMap } from "@/router/types";
import type { TItemRoutes } from "@/types";

export const createRouteNamesFromNamespace = (
  namespace: string
): TItemRoutes => {
  return {
    index: `${namespace}-index` as keyof RouteNamedMap,
    show: `${namespace}-show` as keyof RouteNamedMap,
    share: `${namespace}-share` as keyof RouteNamedMap,
    settings: `${namespace}-settings` as keyof RouteNamedMap,
  };
};

/**
 * Helper function that enforces the route name exists in RouteNamedMap
 * This will cause a TypeScript error if the route doesn't exist
 */
export const route = <T extends keyof RouteNamedMap>(name: T): T => name;

/**
 * School class route constants with strict type checking
 * TypeScript will error if any route name is invalid
 */
export const SCHOOL_CLASS_ROUTES = {
  TEACHER: {
    INDEX: route("teacher-school-class-index"),
    SHOW: route("teacher-school-class-show"),
    PROJECTS: route("teacher-school-class-show-projects"),
    PROJECT_SHOW: route("teacher-school-class-show-projects-project-show"),
    PROJECT_NEW: route("teacher-school-class-project-new"),
    PROJECT_SHARE: route(
      "teacher-school-class-show-projects-project-show-share"
    ),
    PROJECT_EDIT: route("teacher-project-edit"),
    PROJECT_ARCHIVED: route(
      "teacher-school-class-show-projects-project-show-archived"
    ),
    ARCHIVED: route("teacher-school-class-archived"),
    MEMBERS: route("teacher-school-class-members"),
    INVITE_REDEEM: route("school_class_invite_redeem"),
    ACCESS_CODES: route("teacher-school-class-show-access-codes"),
    ACTIVITIES_SHOW: route("teacher-school-class-project-activity-show"),
    PUPIL_PRINT: route("teacher-school-class-pupil-print"),
    INFO: route("teacher-school-class-info"),
    NEW: route("teacher-school-class-new"),
  },
  PUPIL: {
    SHOW: route("pupil-school-class-show"),
    PROJECTS: route("pupil-school-class-projects"),
    PAUSED: route("pupil-school-class-paused"),
    ARCHIVED: route("pupil-school-class-archived"),
    PROJECT_SHOW: route("pupil-school-class-project-show"),
    PROJECT_SHARE: route("pupil-school-class-project-share"),
    LOGOUT_SUCCESS: route("pupil-logout-success"),
  },
} as const;

/**
 * Route groups for the main navbar tabs — each group lists the routes
 * that activate that tab in MainFobizzNavbar/Tabs.vue.
 */
export const NAVBAR_TAB_ROUTES = {
  START: {
    HOME: route("home"),
  },
  ORGANIZE: {
    FOLDERS_INDEX: route("folders-index"),
    SHARED_FOLDERS_INDEX: route("shared-folders-index"),
    SHARED_FOLDERS_SHOW: route("shared-folders-show"),
    TIMETABLE_INDEX: route("timetable-index"),
    TIMETABLE_CREATE: route("timetable-create"),
    TIMETABLE_EVENT_DETAILS: route("timetable-event-details"),
    SCHOOL_CLASS_INDEX: SCHOOL_CLASS_ROUTES.TEACHER.INDEX,
    SCHOOL_CLASS_INFO: SCHOOL_CLASS_ROUTES.TEACHER.INFO,
    SCHOOL_CLASS_NEW: SCHOOL_CLASS_ROUTES.TEACHER.NEW,
    SCHOOL_CLASS_ARCHIVED: SCHOOL_CLASS_ROUTES.TEACHER.ARCHIVED,
    SCHOOL_CLASS_MEMBERS: SCHOOL_CLASS_ROUTES.TEACHER.MEMBERS,
    SCHOOL_CLASS_ACTIVITY_SHOW: SCHOOL_CLASS_ROUTES.TEACHER.ACTIVITIES_SHOW,
    SCHOOL_CLASS_PROJECT_NEW: SCHOOL_CLASS_ROUTES.TEACHER.PROJECT_NEW,
  },
  TOOLS: {
    INDEX: route("tools"),
  },
  MATERIAL: {
    GALLERY_INDEX: route("gallery-index"),
    GALLERY_TO_TEACH: route("gallery-to-teach"),
    GALLERY_COMMUNITY: route("gallery-community"),
    BOOKMARK_INDEX: route("bookmark-index"),
    COLLECTIONS_SHOW: route("collections-show"),
    COLLECTIONS_HOW_TO: route("collections-how-to"),
  },
  PARTNER: {
    INDEX: route("partners-index"),
    INTERNATIONAL_INDEX: route("international-partners-index"),
    SHOW: route("partners-show"),
    INTERNATIONAL_SHOW: route("international-partners-show"),
  },
  COURSES: {
    INDEX: route("courses"),
    SHOW: route("courses-id"),
    ACCESS_INFO: route("courses-id-access-info"),
    DOCUMENTS: route("courses-id-documents"),
    FEEDBACK: route("courses-id-feedback"),
    FEEDBACK_NEW: route("courses-id-feedback-new"),
    LESSON_SHOW: route("courses-id-lessons-lessonId"),
    STATUS: route("courses-id-status"),
    TEACHER: route("courses-id-teacher"),
    COURSE_BOOKMARKS: route("course_bookmarks"),
    COURSE_ENROLLMENTS: route("course_enrollments"),
  },
} as const;

export const ONETIME_SCHOOL_CLASS_NEW_ID = "new";
